// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useRef, useState } from 'preact/hooks';
import {
  api,
  ApiError,
  type ResolveInviteTokenResponse,
  type SharedVaultState,
} from '../api.js';
import {
  acceptCommittedDespiteError,
  activationFinishing,
  contributeAndAcceptSharedVault,
  SHARED_VAULT_TWO_STEP_NOTE,
} from '../shared-vault-accept-flow.js';
import { useSession } from '../session-context.js';
import { Onboarding } from './onboarding.js';
import { HardwareLogin } from '../components/hardware-login.js';
import { Web3AuthEmailCapture } from '../components/web3auth-email-capture.js';
import { Web3AuthLossAck } from '../components/web3auth-loss-ack.js';
import {
  LoginMethodChooser,
  useWeb3AuthLogin,
} from '../components/login-method-chooser.js';
import { PlatformTermsDisclosure } from '../components/platform-terms-disclosure.js';
import { PLATFORM_TERMS_VERSION } from './platform-terms.js';
import {
  KeyBackupCeremony,
  keyBackupGateBlocks,
  useKeyBackupGate,
  KEY_BACKUP_GATE_BLOCKED_ERROR,
} from '../components/key-backup-gate.js';
import '../styles/home.css';
import '../styles/accept.css';
import '../styles/shared-vaults.css';

// ── Pure helpers (exported for unit tests) ────────────────────────────

/** Pull the raw invite token out of a URL fragment (`#token=<raw>`) — the shape
 *  of the claim link the initiator hands out (api-contract §2: token in the
 *  fragment, never a query string, so it stays out of access logs + `Referer`).
 *  Accepts the hash with or without a leading `#`; returns `null` when absent
 *  or empty. */
export function parseInviteToken(hash: string): string | null {
  const h = hash.startsWith('#') ? hash.slice(1) : hash;
  const token = new URLSearchParams(h).get('token');
  return token && token.length > 0 ? token : null;
}

/** Human message for a failed `resolve-token`, keyed on the HTTP status: a
 *  `404` means the link was never valid; a `410` means it was used or expired.
 *
 *  COB-586 — `451` is a geo refusal, and the generic fallback was actively
 *  wrong for it on both counts: nothing "went wrong", and "please try
 *  again" invites a retry that can never succeed from the same place. In
 *  practice a prohibited viewer is short-circuited to `GeoRestricted`
 *  before this renders (the module-level 451 hook raises the app-level
 *  gate, which replaces the whole subtree), so this branch is the
 *  belt-and-braces one — it is what shows if the hook is ever unset. It
 *  still must not lie. */
export function resolveErrorMessage(status: number): string {
  if (status === 404) return "This invitation link isn't valid.";
  if (status === 410) {
    return 'This invitation has expired or has already been used.';
  }
  if (status === 451) {
    return "This invitation can't be opened from your region just yet.";
  }
  return 'Something went wrong loading this invitation. Please try again.';
}

/** Only a still-forming vault (`PENDING_INVITES`) can be accepted. A live token
 *  on any other state (e.g. a vault a co-signer collapsed to `REJECTED`) is
 *  resolvable but not acceptable. */
export function inviteIsAcceptable(state: SharedVaultState): boolean {
  return state === 'PENDING_INVITES';
}

/** Subtitle for the joined view. `activationPending` (COB-338) is the
 *  roster-filling accept whose server-side seal failed — everyone is in and
 *  the backend's lazy reconcile finishes the activation on the vault page's
 *  polls, so it reads as success-in-progress, never an error. */
export function acceptedSubtitle(activationPending: boolean): string {
  return activationPending
    ? 'Everyone has consented — the vault is finishing activation. This completes automatically; open the vault to follow along.'
    : "Your consent is recorded and your signing key stays in your browser. Once every participant has consented and signed to activate the vault, confirming its setup, the vault activates and can receive deposits.";
}

/** Why a resolved-but-unacceptable invite can't be joined. */
export function inviteUnacceptableMessage(state: SharedVaultState): string {
  switch (state) {
    case 'PROPOSED':
    case 'ACTIVE':
    case 'EMPTY':
      return 'This shared vault has already been set up and is no longer accepting new co-signers.';
    case 'REJECTED':
    case 'DELETED':
      return 'This shared vault invitation was cancelled.';
    default:
      return 'This invitation is no longer available.';
  }
}

/** COB-543 (D6): the explicit identity bind, rendered directly above the consent
 *  CTA.
 *
 *  Under email invites the page could check the signed-in account against the
 *  address the slot was issued to, and it refused a mismatch. A claim link has
 *  no such address: the token is the whole credential, so whichever account
 *  happens to be signed in when the link is opened becomes the co-owner — and
 *  that is exactly the accident worth naming. Someone who opened a colleague's
 *  link on a shared machine, or who is signed in as a second personal account,
 *  must see WHICH identity is about to be written onto the roster before they
 *  press the button, not afterwards on a vault they cannot leave.
 *
 *  Deliberately not another click: the consent button is already the deliberate
 *  act, and a second checkbox in front of it would be dismissed unread. What is
 *  required is that the CTA area state the account it will bind. */
function IdentityBindNotice({ userEmail }: { userEmail: string | null }) {
  return (
    <p class="accept-subtitle" data-testid="sv-identity-bind">
      You're joining as <strong>{userEmail ?? 'this account'}</strong>. This
      account becomes a co-owner of the vault and the other co-owners will see
      its email address on the roster. Sign out and sign in again if you meant to
      use a different account.
    </p>
  );
}

// ── Page ───────────────────────────────────────────────────────────────

type Phase = 'resolving' | 'error' | 'ready' | 'accepting' | 'accepted';

export function SharedVaultAccept() {
  const session = useSession();
  // COB-220: offer the full login-method chooser (Self Custody + Web3Auth)
  // inline so a delegated-custody invitee can sign in in-place. The JWT is
  // in-memory only (no session restore on reload), so the bearer token —
  // held in `tokenRef` below — must survive the login without a page
  // navigation; the chooser / onboarding render only while unauthenticated.
  const {
    loginMethod,
    setLoginMethod,
    web3authBusy,
    web3authError,
    collisionEmail,
    setCollisionEmail,
    handleWeb3Auth,
  } = useWeb3AuthLogin();
  // COB-457: joining a p2p vault requires this session's key backup to be
  // confirmed — a lost participant key degrades everyone's quorum. The gate is
  // fail-closed (an unknown or failed probe never unlocks consent) and any
  // remediation happens in-place, because navigating away destroys the
  // in-memory invite token below.
  const backupGate = useKeyBackupGate();
  const [phase, setPhase] = useState<Phase>('resolving');
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<ResolveInviteTokenResponse | null>(null);
  // COB-338: the accepted view's copy — true when our accept filled the roster
  // but the server-side seal is still finishing (the backend reconciles).
  const [activationPending, setActivationPending] = useState(false);

  // The raw bearer token, captured once from the URL fragment on mount. Held in
  // a ref so it survives re-renders — including the embedded onboarding flow an
  // off-platform invitee walks through — and never lands in logged React state.
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    const token = parseInviteToken(window.location.hash);
    tokenRef.current = token;
    if (!token) {
      setError(
        'This invitation link is missing its token. Open the full claim link exactly as it was sent to you.',
      );
      setPhase('error');
      return;
    }
    let cancelled = false;
    api
      .resolveSharedVaultInvite({ invite_token: token })
      .then((r) => {
        if (cancelled) return;
        setResolved(r);
        setPhase('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? resolveErrorMessage(e.status)
            : resolveErrorMessage(0),
        );
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleAccept() {
    const token = tokenRef.current;
    if (!resolved || !token) return;
    if (!session.agent) {
      setError('Your session is locked. Sign in again to consent.');
      return;
    }
    // COB-457 defence in depth: the render gate below already withholds an
    // enabled button, but the handler refuses too — a status that flips
    // between render and click must not slip a consent through.
    if (keyBackupGateBlocks(backupGate.status)) {
      setError(KEY_BACKUP_GATE_BLOCKED_ERROR);
      return;
    }
    setError(null);
    setPhase('accepting');
    try {
      // The accept flow, with the claim link's bearer token as the credential —
      // the only credential there is since COB-543.
      const res = await contributeAndAcceptSharedVault(
        session.agent,
        resolved.vault_id,
        resolved.slot_id,
        token,
      );
      setActivationPending(res.activation_pending);
      setPhase('accepted');
    } catch (e) {
      // COB-338: the claim may have committed even though the POST errored
      // (a response lost in transit) — and the single-use token is then
      // rightly dead, so "retry" would be a dead end. Re-read the vault (we
      // are authenticated, and a committed claim makes us a member) before
      // reporting failure.
      try {
        const fresh = await api.getSharedVault(resolved.vault_id);
        if (acceptCommittedDespiteError(fresh.participants, resolved.slot_id)) {
          setActivationPending(activationFinishing(fresh));
          setPhase('accepted');
          return;
        }
      } catch {
        // Probe failed — fall through to the original accept error.
      }
      setError(e instanceof Error ? e.message : 'Could not record your consent.');
      setPhase('ready');
    }
  }

  if (phase === 'resolving') {
    return (
      <div class="accept-page">
        <div class="accept-loading" role="status" aria-live="polite">
          <div class="accept-spinner" aria-hidden="true" />
          <p>Loading your invitation…</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div class="accept-page">
        <h2 class="accept-title">Invitation unavailable</h2>
        <p class="accept-error">{error}</p>
        <div class="accept-actions">
          <a href="/" class="btn btn-secondary">
            Back to home
          </a>
        </div>
      </div>
    );
  }

  if (!resolved) return null;

  if (phase === 'accepted') {
    return (
      <div class="accept-page">
        <h2 class="accept-title">You've joined the shared vault</h2>
        <p class="accept-subtitle">{acceptedSubtitle(activationPending)}</p>
        <div class="accept-actions">
          <a href={`/shared-vault/${resolved.vault_id}`} class="btn btn-primary">
            View the shared vault
          </a>
        </div>
      </div>
    );
  }

  if (!inviteIsAcceptable(resolved.state)) {
    return (
      <div class="accept-page">
        <h2 class="accept-title">Invitation unavailable</h2>
        <p class="accept-subtitle">{inviteUnacceptableMessage(resolved.state)}</p>
        <div class="accept-actions">
          <a href="/" class="btn btn-secondary">
            Back to home
          </a>
        </div>
      </div>
    );
  }

  // phase is 'ready' or 'accepting' on an acceptable invite.
  //
  // COB-543: there is no invitee-email gate here any more, and there cannot be
  // one — the slot has no address attached, and the token IS the credential.
  // Whichever account is signed in when the link is opened is the account that
  // gets bound to the slot, so the CTA below names it explicitly rather than
  // letting the binding happen silently.
  return (
    <div class="accept-page">
      <h2 class="accept-title">You're invited to a shared vault</h2>
      <p class="accept-subtitle">
        {resolved.inviter_email ? (
          <>
            <strong>{resolved.inviter_email}</strong> invited you to co-sign a
            Cobault shared vault.
          </>
        ) : (
          <>You've been invited to co-sign a Cobault shared vault.</>
        )}{' '}
        A shared vault is a multi-signature Bitcoin vault co-owned by several
        people; funds move only when a quorum of co-owners signs. Your signing
        key is generated and held in your browser — Cobault never sees it.
      </p>
      <p class="accept-subtitle">{SHARED_VAULT_TWO_STEP_NOTE}</p>

      {/* COB-543 (D3): the three facts a claimant can check before they
          contribute a key. `inviter_email` is an ACCOUNT email the platform can
          vouch for; `label` is a string the initiator typed — the claimant's own
          cross-check that they were handed the right link, never an assertion by
          us; the quorum states the commitment in `m`-of-`n` terms so it is
          legible before the key is contributed rather than after. */}
      <dl class="confirm-card">
        <div class="confirm-row">
          <dt>Invited by</dt>
          <dd class="mono">{resolved.inviter_email}</dd>
        </div>
        {resolved.label && (
          <div class="confirm-row">
            <dt>Slot</dt>
            <dd>for the slot labelled “{resolved.label}”</dd>
          </div>
        )}
        <div class="confirm-row">
          <dt>Vault</dt>
          <dd class="tabular-nums">
            a {resolved.quorum_m}-of-{resolved.participant_count} shared vault
          </dd>
        </div>
      </dl>

      {error && <p class="accept-error">{error}</p>}

      {phase === 'accepting' ? (
        <div class="accept-loading" role="status" aria-live="polite">
          <div class="accept-spinner" aria-hidden="true" />
          <p>Recording your consent…</p>
        </div>
      ) : session.isAuthenticated ? (
        backupGate.status === 'unconfirmed' ? (
          // The consent affordance is replaced, not merely disabled — an
          // unbacked invitee gets the remedy in its place. "Not now" stays so
          // there is still a way out of the page.
          <>
            <KeyBackupCeremony onConfirmed={backupGate.refresh} />
            <div class="accept-actions">
              <a href="/shared-vaults" class="btn btn-secondary">
                Not now
              </a>
            </div>
          </>
        ) : (
          <>
            {/* COB-512 / COB-521: the terms, clause 8's risks and the
                participant-visibility disclosures, in place and above the
                button. The per-vault key is derived inside
                `contributeAndAcceptSharedVault`, which only `handleAccept`
                calls — so everything rendered here is necessarily shown
                before any key exists, on every login method, since all of
                them converge on this one button. */}
            <PlatformTermsDisclosure surface="accept" />
            <IdentityBindNotice userEmail={session.userEmail} />
            <div class="accept-actions">
              <a href="/shared-vaults" class="btn btn-secondary">
                Not now
              </a>
              {/* `unknown` (probe in flight) renders the familiar button
                  disabled rather than the ceremony — fail-closed without a
                  flash of remediation UI for the already-backed-up common
                  case. */}
              <button
                type="button"
                class="btn btn-primary"
                disabled={!backupGate.satisfied}
                onClick={handleAccept}
              >
                Consent to participate
              </button>
            </div>
          </>
        )
      ) : (
        <div class="sv-accept-onboarding">
          <p class="accept-subtitle">
            Sign in or set up your Cobault account to consent. Whichever account
            you use becomes this vault's co-owner for this slot, and the other
            co-owners will see its email address on the roster.
          </p>
          {loginMethod === null && (
            <LoginMethodChooser
              onSelect={setLoginMethod}
              onWeb3Auth={handleWeb3Auth}
              web3authBusy={web3authBusy}
              web3authError={web3authError}
            />
          )}
          {loginMethod === 'local' && (
            <>
              {collisionEmail && (
                <p class="login-collision-notice">
                  You already have a Cobault account with{' '}
                  <strong>{collisionEmail}</strong>. Sign in with the method you
                  used when you created it — if that was a passcode, enter it
                  below.
                </p>
              )}
              <Onboarding
                onBack={() => {
                  setLoginMethod(null);
                  setCollisionEmail(null);
                }}
                initialEmail={collisionEmail ?? undefined}
              />
            </>
          )}
          {/* HW.3: hardware (Ledger) sign-in. COB-543: nothing to prefill — the
              slot carries no address, so the claimant types their own. */}
          {loginMethod === 'hardware' && (
            <HardwareLogin onBack={() => setLoginMethod(null)} />
          )}
          {/* COB-345: email capture for a provider-less Web3Auth sign-in. On
              success the typed email becomes the session email, which is the
              identity the claim then binds to this slot. */}
          {loginMethod === 'web3auth-email' && (
            <Web3AuthEmailCapture onBack={() => setLoginMethod(null)} />
          )}
          {/* COB-272: login-loss acknowledgement gating a new provider-email
              Web3Auth account's deferred register. A collision bounces to
              Self Custody with the email prefilled, mirroring the chooser. */}
          {loginMethod === 'web3auth-ack' && (
            <Web3AuthLossAck
              onBack={() => setLoginMethod(null)}
              onCollision={(email) => {
                setCollisionEmail(email);
                setLoginMethod('local');
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
